import React from "react";
import ReactDOM from "react-dom/client";
import {BrowserRouter} from "react-router-dom";

import "./index.css";
import App from "./App";
import {AuthProvider} from "./auth/authStore";
import {NotificationsProvider} from "./notifications/notificationsStore";
import {WsProvider} from "./ws/wsStore";
import {ToastProvider} from "./ui/toast/ToastProvider";
import {FavoritesProvider} from "./favorites/favoritesStore";
import {HelmetProvider} from "react-helmet-async";
import {
    getServiceWorkerDiagnostics,
    isServiceWorkerEnabledByEnv,
    registerAppServiceWorker,
    unregisterAppServiceWorkers,
} from "./pwa/swRegistration";
import { initInstallPromptListeners } from "./pwa/installPrompt";
import { initFirebaseAnalytics, initFirebaseApp } from "./firebase/app";
import { bootstrapTheme } from "./utils/theme";
import { BottomPlayerProvider } from "./podcasts/BottomPlayerProvider";
import { ReportContentProvider } from "./components/reports/ReportContentProvider";

const root = ReactDOM.createRoot(document.getElementById("root"));
bootstrapTheme();

root.render(
    <BrowserRouter>
        <AuthProvider>
            <WsProvider>
                <ToastProvider>
                    <NotificationsProvider>
                        <FavoritesProvider>
                            <HelmetProvider>
                                <BottomPlayerProvider>
                                    <ReportContentProvider>
                                        <App/>
                                    </ReportContentProvider>
                                </BottomPlayerProvider>
                            </HelmetProvider>
                        </FavoritesProvider>
                    </NotificationsProvider>
                </ToastProvider>
            </WsProvider>
        </AuthProvider>
    </BrowserRouter>
);

const isReactSnap = typeof navigator !== "undefined" && /ReactSnap/i.test(String(navigator.userAgent || ""));
const env = typeof process !== "undefined" && process?.env ? process.env : {};
const isProd = String(env.NODE_ENV || "development") === "production";

function logPwaWarning(message, extra) {
    if (typeof console === "undefined") return;
    // In production we still keep warnings: otherwise a failed SW registration is silent
    // and Push looks enabled in UI while the browser has no worker.
    console.warn(`[PWA] ${message}`, extra || "");
}

function bootPwaRuntime() {
    if (isReactSnap) return;

    initFirebaseApp();
    initFirebaseAnalytics().catch(() => {});
    initInstallPromptListeners();

    const diag = getServiceWorkerDiagnostics();
    if (isServiceWorkerEnabledByEnv()) {
        registerAppServiceWorker({
            onSuccess: () => {
                if (!isProd) console.info("[PWA] Service Worker registered", diag.url);
            },
            onUpdate: () => {
                if (!isProd) console.info("[PWA] Service Worker update found", diag.url);
            },
        }).catch((err) => {
            logPwaWarning("Service Worker registration failed", {
                error: err?.message || String(err),
                diagnostics: diag,
            });
        });
    } else {
        logPwaWarning("Service Worker runtime disabled", diag);
        if (!isProd) unregisterAppServiceWorkers().catch(() => {});
    }
}

if (typeof window !== "undefined") {
    if (document.readyState === "complete") {
        bootPwaRuntime();
    } else {
        window.addEventListener("load", bootPwaRuntime, { once: true });
    }
}
